import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotifierService } from 'angular-notifier';
import { BoobleModalService } from 'booble-modal';
import { OrdersService } from 'src/app/shared/services/orders.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-settlement-info-confirm',
  templateUrl: './settlement-info-confirm.component.html',
  styleUrls: ['./settlement-info-confirm.component.scss'],
})
export class SettlementInfoConfirmComponent implements OnInit {
  boobleModalData: any;
  loading: boolean;
  private readonly notifier: NotifierService;
  formSettlement!: FormGroup;
  formSettlementInbound!: FormGroup;
  private destroy$ = new Subject<void>();

  constructor(
    private modalService: BoobleModalService,
    notifierService: NotifierService,
    formBuilder: FormBuilder,
    private readonly ordersService: OrdersService,
  ) {
    this.notifier = notifierService;

    this.formSettlement = formBuilder.group({
      contractSettlementNumber: ['', [Validators.pattern('^[0-9]*$')]],
      exchangeContractFile: [null],
      proofOperationFile: [null],
      proofOperationLater: [false],
      sendEmail: [false],
    });

    this.formSettlementInbound = formBuilder.group({
      contractSettlementNumber: ['', [Validators.pattern('^[0-9]*$')]],
      exchangeContractFile: [null],
      sendEmail: [false],
    });
  }

  get settlement(): any {
    return this.formSettlement.value;
  }

  get settlementInbound(): any {
    return this.formSettlementInbound.value;
  }

  getFileName(value: any): string {
    if (!value) return '';
    if (value instanceof File) {
      return value.name;
    }
    if (typeof value === 'string') {
      const parts = value.split('-');
      if (parts.length > 5) {
        return parts.slice(5).join('-');
      }
      return value;
    }
    return value?.name || value;
  }

  ngOnInit() {
    if (typeof this.boobleModalData === 'number') {
      this.loading = true;
      this.ordersService
        .getOrderById(this.boobleModalData, 'false')
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (result) => {
            this.loading = false;
            this.boobleModalData = result.data;
            this.initializeComponent();
          },
          error: (err) => {
            this.loading = false;
          },
        });
    } else {
      this.initializeComponent();
    }
  }

  initializeComponent(): void {
    this.patchFormWithOrderSettlement();
  }

  closeModal() {
    this.modalService.dismiss(null);
  }

  proofOperationFileChange(formGroup: FormGroup, event: Event) {
    const file = (event.target as HTMLInputElement).files[0];
    formGroup.patchValue({ proofOperationFile: file });
  }

  exchangeContractFileChange(formGroup: FormGroup, event: Event) {
    const file = (event.target as HTMLInputElement).files[0];
    formGroup.patchValue({ exchangeContractFile: file });
  }

  patchFormWithOrderSettlement(): void {
    const orderSettlement = this.boobleModalData?.orderSettlement;
    if (!orderSettlement) return;

    const {
      contractSettlementNumber,
      exchangeContractFile,
      proofOperationFile,
    } = orderSettlement;

    if (this.boobleModalData?.remmitanceType === 'INBOUND') {
      this.formSettlementInbound.patchValue({
        contractSettlementNumber,
        exchangeContractFile,
        sendEmail: true,
      });
    } else if (this.boobleModalData?.remmitanceType === 'OUTBOUND') {
      this.formSettlement.patchValue({
        contractSettlementNumber,
        exchangeContractFile,
        proofOperationFile,
        proofOperationLater: !!proofOperationFile,
        sendEmail: true,
      });
    }
  }

  validationForm(formGroup: FormGroup): { [key: string]: any } | null {
    return null;
  }

  resetControl(controlName: string) {
    this.formSettlement.get(controlName)?.reset();
    this.formSettlement.get(controlName)?.setValue(null);
    this.formSettlement.get(controlName)?.markAsPristine();
    this.formSettlement.get(controlName)?.markAsUntouched();

    if (this.boobleModalData?.orderSettlement) {
      delete this.boobleModalData.orderSettlement[controlName];
    }

    this.formSettlement.updateValueAndValidity();
  }

  resetExchangeContractFile() {
    this.formSettlementInbound.get('exchangeContractFile')?.reset();
    this.formSettlementInbound.get('exchangeContractFile')?.setValue(null);
    this.formSettlementInbound.get('exchangeContractFile')?.markAsPristine();
    this.formSettlementInbound.get('exchangeContractFile')?.markAsUntouched();
    this.formSettlementInbound.get('exchangeContractFile')?.setErrors(null);
    this.formSettlementInbound.updateValueAndValidity();
  }

  settlementConfirm() {
    this.loading = true;

    const formData = new FormData();

    if (this.boobleModalData.remmitanceType === 'INBOUND') {
      if (this.settlementInbound?.contractSettlementNumber) {
        formData.append(
          'ContractSettlementNumber',
          this.settlementInbound.contractSettlementNumber,
        );
      }

      if (this.settlementInbound?.exchangeContractFile instanceof File) {
        formData.append(
          'ExchangeContract',
          this.settlementInbound.exchangeContractFile,
        );
      }

      const hasDocumentsInbound = this.settlementInbound?.exchangeContractFile instanceof File;
      formData.append('SendEmail', hasDocumentsInbound && (this.settlementInbound?.sendEmail ?? false));
    } else if (this.boobleModalData?.remmitanceType === 'OUTBOUND') {
      if (this.settlement?.contractSettlementNumber) {
        formData.append(
          'ContractSettlementNumber',
          this.settlement.contractSettlementNumber,
        );
      }

      if (this.settlement?.exchangeContractFile instanceof File) {
        formData.append(
          'ExchangeContract',
          this.settlement.exchangeContractFile,
        );
      }

      if (this.settlement?.proofOperationFile instanceof File) {
        formData.append('ProofOperation', this.settlement.proofOperationFile);
      }

      formData.append(
        'proofOperationLater',
        this.settlement?.proofOperationLater ?? false,
      );
      const hasDocumentsOutbound =
        this.settlement?.exchangeContractFile instanceof File ||
        this.settlement?.proofOperationFile instanceof File;
      formData.append('SendEmail', hasDocumentsOutbound && (this.settlement?.sendEmail ?? false));
    }

    const request$ = this.ordersService.seetlementConfirm(
      this.boobleModalData?.id,
      formData,
    );

    request$.subscribe({
      next: () => {
        this.loading = false;
        this.closeModal();
        this.notifier.notify(
          'success',
          'Ação realizada com sucesso.',
        );
        this.ordersService.updateOrderView();
      },
      error: (err) => {
        this.loading = false;
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
